// Copyright (c) Docugami, Inc. All rights reserved.
import { useState, useEffect } from 'react';
import { ChunkInstance } from '../../networking/api';
import { useAaDgWordCanvasProxy } from '../../hooks/aaDgWordCanvasProxy';
import { AaScroller, AaCardsList } from './ClauseSuggestionsList';
import { DgVerticalSpacer, DgSelectableAndHoverable, DgFlexCenteredColumn, DgNonSelectableLabelCss, DgSingleLineEllipsis, DgFlexSpacer } from '../common/DgCommonStyledComponents';
import styled from 'styled-components';
import { homeGray, primaryTheme } from '../shell/presentation/AppStyles';
import { DgOnFieldValueChange } from '../doc_hosting/doc_hosts/word/v3/DgOnFieldValueChange';
import { TextField } from "@fluentui/react";
import { useDocumentDgml } from 'hooks/documentDgml';
import { dgTagFontFamily, userFontFamily } from 'components/common/Fonts';
import { getRecommendedTagsHelper } from 'hooks/LabelChunk';
import { useRef } from 'react';
import { decodeLabel } from 'utils/TagStandardUtils';
import { WordTaskpaneLoadingPage } from './WordTaskpane';
import { useGetLabelsQuery } from 'store/frontend/frontendSlice';
import { createFolderId } from 'components/file_management/file_helpers';

const FieldColorStripe = styled.div<{selected: boolean}>`
    background: #56CCF2;
    border-radius: 5px 0px 0px 5px;
    width: ${props => props.selected ? "10px" : "5px"};
    height: 100%;
    transition: all 0.3s linear;
`;

const FieldCardContainer = styled.div<DgSelectableAndHoverable>`
    background: ${primaryTheme.palette.white};
    box-shadow: 0px 0px 5px rgba(0, 0, 0, 0.15);
    border-radius: 5px;
    display: flex;
    flex-direction: row;
    overflow: hidden;
    width: ${props => props.selected ? '307px' : '302px'};
    height: ${props => props.selected ? '100px' : '60px'};
    margin-left: ${props => props.selected ? '0' : '5px'};
    transition: all 0.3s linear;
`;

const FieldCardLabel = styled(DgSingleLineEllipsis)`
    font-family: ${dgTagFontFamily};
    font-style: normal;
    font-weight: 700;
    font-size: 18px;
    line-height: 21px;
    ${DgNonSelectableLabelCss}
    color: ${primaryTheme.palette.black};
`;

const FieldCardValue = styled(DgSingleLineEllipsis)`
    font-family: ${userFontFamily};
    font-style: normal;
    font-weight: 400;
    font-size: 18px;
    line-height: 22px;
    color: ${homeGray};
`;

const FieldCardEditValue = styled(TextField)`
    font-family: ${userFontFamily};
    font-style: normal;
    font-weight: 400;
    font-size: 18px;
    line-height: 22px;
    color: ${homeGray};
`;

const AaCardPaddedContent = styled.div`
    width: 279px;
`;

export interface WordFieldsCardProps {
    currentField: string;
    documentId: string;
}

export const WordFieldsCard = (props: WordFieldsCardProps): JSX.Element => {
    const [initialized, setInitialized] = useState(false);
    const [updatingValueInWord, setUpdatingValueInWord] = useState(false);
    const [fieldValues, setFieldValues] = useState<{[id: string]: string}>({});
    const [hoveredId, setHoveredId] = useState("");
    const [selectedId, setSelectedId] = useState("");
    const [fieldValueChange, setFieldValueChange] = useState<DgOnFieldValueChange | undefined>();
    const [fieldInstances, setFieldInstances] = useState<ChunkInstance[]>([]);

    const delayedFieldUpdatesRef = useRef<DgOnFieldValueChange[]>([]);

    const { clusterId } = createFolderId(props.documentId)
    const { data: labelState } = useGetLabelsQuery(clusterId)

    const dgWordCanvasProxy = useAaDgWordCanvasProxy(props.documentId);
    const dgml = useDocumentDgml();

    useEffect(() => {
        if (dgWordCanvasProxy && !initialized) {
            setInitialized(true);
            dgWordCanvasProxy.getFieldValues()
                .then((values) => {
                    setFieldValues(values);
                });

            dgWordCanvasProxy.onFieldValueChange.on((dgEventArgs: DgOnFieldValueChange | undefined) =>
            {
                setFieldValueChange(dgEventArgs);
            });
        }
    }, [dgWordCanvasProxy, initialized])

    useEffect(() => {
        if (fieldValueChange)
        {
            setFieldValues({
                ...fieldValues,
                [fieldValueChange.DgmlSpanId]: fieldValueChange.NewValue
            })

            setFieldValueChange(undefined);
        }
    }, [fieldValueChange, fieldValues])

   useEffect(() => {
        if (!dgml.isInitialized() || !dgWordCanvasProxy?.initialized) {
            setFieldInstances([]);
            return;
        }

        const fieldSet = new Set(dgWordCanvasProxy?.fieldSpanIdList);
        const instances = dgml
            .getInstancesFlat()
            .filter((x: ChunkInstance) =>  fieldSet.has(x.spanId!))
            .sort((a: ChunkInstance, b: ChunkInstance) => {
                if (a.tokenRange!.fromTok > b.tokenRange!.fromTok) {
                    return 1;
                }

                if (a.tokenRange!.fromTok === b.tokenRange!.fromTok) {
                    if (a.tokenRange!.toTok === b.tokenRange!.toTok) {
                        return 0;
                    }

                    if (a.tokenRange!.toTok < b.tokenRange!.toTok) {
                        return 1;
                    }
                }

                return -1;
            });

        setFieldInstances(instances);
    }, [dgml, dgWordCanvasProxy])

    function updateWord(fieldId: string, value: string, skipCheck: boolean=false) {
        if (!updatingValueInWord || skipCheck) {
            setUpdatingValueInWord(true);
            dgWordCanvasProxy?.setFieldValue(fieldId, value)
                .then(x => {
                    if (!delayedFieldUpdatesRef.current?.length) {
                        setUpdatingValueInWord(false);
                    } else {
                        const delayedFiledUpdate = delayedFieldUpdatesRef.current!.shift();
                        updateWord(delayedFiledUpdate!.DgmlSpanId, delayedFiledUpdate!.NewValue, skipCheck=true);
                    }
                });
        } else {
            if (delayedFieldUpdatesRef.current!.length > 0 && delayedFieldUpdatesRef.current![delayedFieldUpdatesRef.current!.length - 1].DgmlSpanId === fieldId) {
                delayedFieldUpdatesRef.current![delayedFieldUpdatesRef.current!.length - 1].NewValue = value;
            } else {
                delayedFieldUpdatesRef.current!.push({DgmlSpanId: fieldId, NewValue: value});
            }
        }
    }

    function getFieldSection(chunkInstance: ChunkInstance): JSX.Element {
        const fieldId = chunkInstance.spanId as string;
        let label = 'Field';
        const labelItem = labelState?.entities[chunkInstance.labelId ?? ''];
        if (labelItem) {
            label = labelItem.label as string;
        } else {
            const labels = getRecommendedTagsHelper(chunkInstance, "");
            if (!!labels && labels.length > 0) {
                label = labels[0].value;
            }
        }

        let value = "";
        if (fieldId in fieldValues)
        {
            value = fieldValues[fieldId];
        }

        const hovered = hoveredId === fieldId;
        const selected = selectedId === fieldId;

        return <FieldCardContainer
                key={`fieldcc_${fieldId}`}
                selected={selected}
                hovered={hovered}
                onMouseEnter={() => setHoveredId(fieldId)}
                onMouseLeave={() => setHoveredId("")}
                onClick={() => {
                    setSelectedId(fieldId);
                    dgWordCanvasProxy?.selectField(fieldId);
                }}>
            <FieldColorStripe selected={selected} />
            <DgFlexCenteredColumn style={{flex: 1}}>
                <DgVerticalSpacer height="7px"/>
                <AaCardPaddedContent>
                    <FieldCardLabel>{decodeLabel(label)}</FieldCardLabel>
                </AaCardPaddedContent>
                <DgFlexSpacer/>
                <AaCardPaddedContent>
                    { selected ?
                        <FieldCardEditValue
                            defaultValue={value}
                            onChange={(e, t) => {
                                updateWord(fieldId, t as string);
                                setFieldValueChange({DgmlSpanId: fieldId, NewValue: t as string});
                            }}/> :
                        <FieldCardValue>{value}</FieldCardValue>
                    }
                </AaCardPaddedContent>
                <DgFlexSpacer/>
                <DgVerticalSpacer height="2px"/>
            </DgFlexCenteredColumn>
        </FieldCardContainer>
    }

    if (!initialized) {
        return <WordTaskpaneLoadingPage />
    }

    return (
        <div key="tp" className="taskpanePage">
            <AaScroller key="s">
                <AaCardsList key="cl">
                    <DgVerticalSpacer key="vs" height={"7px"}/>
                    { fieldInstances.map(f => [getFieldSection(f), <DgVerticalSpacer key={`vs_${f.spanId}`} height={"7px"}/>])}
                </AaCardsList>
            </AaScroller>
        </div>
    )
}
